﻿import { TweenMax, TimelineMax, gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { sitecore, MediaQuery } from '../../Foundation';
import objectFitImages from 'object-fit-images';

export class FeatureScroll {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
    this.headElement = document.querySelector('#header');
    this.offset = this.headElement ? this.headElement.getBoundingClientRect().height : 0;

    if (sitecore.isInPageEditor) {
      return false;
    }

    this.initialize();

    let _this = this;

    // On lazy loading, refresh
    window.addEventListener('image.loaded', () => {
      ScrollTrigger.refresh();
    });

    // Reflow recalculation
    window.addEventListener('change.mq', function () {
      const scrollAnims = [
        _this.startFeaturesPinnedScrollAnimationRef,
        _this.headingScrollAnimationRef,
        _this.startImageScrollAnimationRef,
      ];
      _this.cleanAnimations(scrollAnims);
      _this.initialize();
    });
  }

  initialize() {
    this.initializeElements();
    this.initializePlugin();
    this.headingScrollAnimationRef = this.headingScrollAnimation();
    this.startImageScrollAnimationRef = this.startImageScrollAnimation();
    this.startFeaturesPinnedScrollAnimationRef = this.startFeaturesPinnedScrollAnimation();

    // IE11
    const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
    if (isIE11) {
      const arrImages = Array.from(this.element.querySelectorAll('img'));
      objectFitImages(arrImages);
    }
  }

  updateScrollAnimations() {
    const scrollAnims = [
      this.startFeaturesPinnedScrollAnimationRef,
      this.headingScrollAnimationRef,
      this.startImageScrollAnimationRef,
    ];
    this.cleanAnimations(scrollAnims);
  }

  cleanAnimations(scrollTriggerAnims) {
    for (var i = 0; i < scrollTriggerAnims.length; i++) {
      if (scrollTriggerAnims[i] == null || scrollTriggerAnims[i] == undefined) {
        continue;
      }
      scrollTriggerAnims[i].disable();
      scrollTriggerAnims[i].animation.seek(0).invalidate();
      this.clearProps(scrollTriggerAnims[i].animation);
      scrollTriggerAnims[i].animation.kill();
      scrollTriggerAnims[i].kill();
      scrollTriggerAnims[i] = undefined;
    }
  }

  clearProps(scene) {
    var targets = scene.getChildren();
    for (var i = 0; i < targets.length; i++) {
      TweenMax.set(targets[i]._targets, {
        clearProps: 'all',
      });
    }
  }

  clearAllProps(timeLines) {
    for (var x = 0; x < timeLines.length; x++) {
      this.clearProps(timeLines[x]);
    }
  }

  initializePlugin() {
    if (gsap.ScrollTrigger === undefined) {
      gsap.registerPlugin(ScrollTrigger);
    }
  }

  initializeElements() {
    let arrMediaItems = Array.from(this.element.querySelectorAll('.feature-scroll__media-item')),
      oFallbackImage = arrMediaItems.filter((x) => x.classList.contains('is-fallback'));

    if (arrMediaItems.length > 1) {
      // We have images within section, remove fallback in case it should be set
      if (oFallbackImage.length) {
        oFallbackImage[0].remove();
      }
    }

    // Filter out fallback from mediaItems
    arrMediaItems = arrMediaItems.filter((x) => !x.classList.contains('is-fallback'));

    // Continue to clone items
    arrMediaItems.forEach((x) => {
      // Validate if item has content within
      const hasChildren = Array.from(x.children);
      if (hasChildren) {
        const oClone = x.cloneNode(true);
        this.mediaContainerElem.appendChild(oClone);
        // Remove node from dom
        x.remove();
      }
    });

    // Set height to list items container if animation is stacked
    this.setSlidesHeight();
  }

  setSlidesHeight() {
    // Set height to list items container if animation is stacked
    if (this.element.classList.contains('feature-scroll--stacked') && this.featuresListElem) {
      this.featuresListElem.style.height = this.getSlidesListHeight() + 'px';
    }
  }

  getSlidesListHeight() {
    const featuresArray = Array.from(this.featuresListElems);
    let height = 0;
    featuresArray.forEach((feature, i) => {
      if (featuresArray.length == i + 1) {
        height += feature.offsetHeight;
      } else {
        height +=
          feature.offsetHeight -
          feature.querySelector('.feature-scroll__listitem__content').offsetHeight;
      }
    });
    return height;
  }

  getFeaturesAnimationTimeline(spot) {
    let oTimeline = new TimelineMax();
    oTimeline = spot.featuresListAnimation(oTimeline);
    return oTimeline;
  }

  startImageScrollAnimation() {
    const _this = this;

    let oTimeline = this.imageAndFeaturesIntroAnimation();

    let st = ScrollTrigger.create({
      trigger: _this.element,
      start: 'top 50%',
      end: '+=800',
      scrub: 0.3,
      animation: oTimeline,
    });

    return st;
  }

  startFeaturesPinnedScrollAnimation() {
    const _this = this;
    let animationTimeline = _this.getFeaturesAnimationTimeline(_this);
    const animationEndConst = 400;
    let scrollAnimationEnd = animationEndConst + this.featuresListElems.length * animationEndConst;
    ScrollTrigger.saveStyles();
    let st = ScrollTrigger.create({
      trigger: _this.contentElem,
      pin: true,
      start: 'top-=' + this.offset + 'px',
      end: '+=' + scrollAnimationEnd,
      scrub: 0.1,
      animation: animationTimeline,
    });
    ScrollTrigger.saveStyles(_this.contentElem);

    return st;
  }

  clearInlineStyle(elems) {
    if (!elems) return;
    var i = elems.length;

    if (i !== undefined) {
      while (i--) {
        elems[i].removeAttribute('style');
      }
      return;
    }

    elems.removeAttribute('style');
  }

  headingScrollAnimation() {
    if (this.introElem == null) {
      return;
    }

    const _this = this;

    let oTimeline = this.headingAnimation();

    let st = ScrollTrigger.create({
      trigger: _this.element,
      start: 'top 70%',
      end: '+=300',
      scrub: 0.3,
      animation: oTimeline,
    });

    return st;
  }

  headingAnimation() {
    let oTimeline = new TimelineMax();

    let animationDuration = 5;

    // Line-Height for animation
    let lineHeightValue = '100px';
    if (MediaQuery.below('md')) {
      /* eslint-disable-next-line */
      lineHeightValue = '70px';
    }

    // OpacityAnimation
    const opacityAnimationFrom = {
      opacity: 0,
    };
    const opacityAnimationTo = {
      duration: animationDuration,
      opacity: 1,
      ease: 'power3.in',
    };

    oTimeline.addLabel('startHeadingAnimation');
    oTimeline.fromTo(
      this.introElem,
      opacityAnimationFrom,
      opacityAnimationTo,
      'startHeadingAnimation',
    );

    return oTimeline;
  }

  imageAndFeaturesIntroAnimation() {
    let oTimeline = new TimelineMax();

    oTimeline.addLabel('fadeInStart');

    const animationDuration = 0.1;

    const animationFrom = { opacity: 0 };
    const animationTo = {
      duration: animationDuration,
      opacity: 1,
      ease: 'power2.in',
      onComplete: () => this.playInternalVideo(0),
    };

    oTimeline.fromTo(this.mediaContainerElem, animationFrom, animationTo, 'fadeInStart');
    if (MediaQuery.above('lg')) {
      oTimeline.fromTo(this.featuresListElem, animationFrom, animationTo, 'fadeInStart');
    }

    return oTimeline;
  }

  featuresListAnimation(oTimeline) {
    oTimeline.addLabel('startTimeLabel');

    if (this.isStacked) {
      oTimeline = this.featuresListAnimationStacked(oTimeline);
    } else {
      oTimeline = this.featuresListAnimationNonStacked(oTimeline);
    }
    if (MediaQuery.below('lg')) {
      oTimeline.addPause(1.2);
    } else {
      oTimeline.addPause(1.4);
    }

    return oTimeline;
  }

  featuresListAnimationNonStacked(oTimeline) {
    let mobileSlidesFadeOutDuration = 0.1;

    let animationDuration = this.getNonStackedAnimationDuration();

    let slidesTimeOffset = this.getNonStackedSlidesTimeOffset(animationDuration);

    let timeLabelOffset = this.getFeaturesAnimationTimeOffset(0, slidesTimeOffset);
    let timeLabel = 'startTimeLabel+=' + timeLabelOffset;

    const featuresArray = Array.from(this.featuresListElems);

    featuresArray.forEach((feature, i) => {
      if (i > 0) {
        timeLabelOffset = this.getFeaturesAnimationTimeOffset(i, slidesTimeOffset);
        timeLabel = 'startTimeLabel+=' + timeLabelOffset;

        let timeImageChangeLabelOffset = timeLabelOffset + animationDuration / 3,
          imageChangeTimeLabel = 'startTimeLabel+=' + timeImageChangeLabelOffset,
          arrMediaItems = Array.from(this.element.querySelectorAll('.feature-scroll__media-item'));

        if (MediaQuery.below('lg')) {
          // Fade out previous feature slide
          oTimeline.to(
            this.featuresListElems[i - 1],
            { duration: mobileSlidesFadeOutDuration, opacity: 0 },
            timeLabel,
          );
        }

        if (arrMediaItems[i] !== undefined) {
          oTimeline.set(arrMediaItems[i], { display: 'block' }, imageChangeTimeLabel);
          // Feature Image opacity fade in and out animation (opacity)
          const mediaFadeInAnimation = {
            duration: 0.07,
            autoAlpha: 1,
            opacity: 1,
            onComplete: () => this.playInternalVideo(i),
            onReverseComplete: () => {
              this.pauseInternalVideo(i);
              this.playInternalVideo(i - 1);
            },
          };
          oTimeline.fromTo(
            arrMediaItems[i],
            { autoAlpha: 0, opacity: 0 },
            mediaFadeInAnimation,
            '>',
          );

          //Fade out previous feature image
          oTimeline.set(
            arrMediaItems[i - 1],
            { display: 'none', onComplete: () => this.pauseInternalVideo(i - 1) },
            imageChangeTimeLabel,
          );
        }
      }

      // Feature Slide movement over Y axis
      if (MediaQuery.below('lg')) {
        // Feature Slide opacity fade in animation (opacity)
        oTimeline.to(feature, { duration: animationDuration, opacity: 1 }, timeLabel);
        // Feature Slide Y Translation
        oTimeline.fromTo(
          feature,
          { y: this.getFeaturesAnimationYOffsetFromMobile(feature) },
          { y: this.getFeaturesAnimationYOffsetToMobile(feature), duration: animationDuration },
          timeLabel,
        );
      } else {
        // Feature Slide Y Translation

        let fromY = this.getFeaturesAnimationYOffsetFromDesktop(feature);
        let toY = this.getFeaturesAnimationYOffsetToDesktop(feature);
        let translationDuration = animationDuration * 1.5;

        // If first slide, start in the middle of the screen
        if (i == 0) {
          fromY = this.getCenteredVerticalPosition(feature);
        }

        // If it's last slide, dont fade it out, and leave it in the middle of the screen.
        if (featuresArray.length == i + 1) {
          fromY = this.getFeaturesAnimationYOffsetFromDesktop(feature);
          toY = this.getCenteredVerticalPosition(feature);
        }

        oTimeline.fromTo(
          feature,
          { y: fromY, ease: 'none' },
          { y: toY, duration: translationDuration, ease: 'none' },
          timeLabel,
        );

        // Fade In
        const fadeInOutAnimationDuration = animationDuration;

        if (i === 0) {
          oTimeline.fromTo(
            feature,
            { opacity: 1 },
            { opacity: 1, duration: fadeInOutAnimationDuration / 3 },
            timeLabel,
          );
        } else {
          oTimeline.to(
            feature,
            {
              opacity: 1,
              duration: fadeInOutAnimationDuration,
              onComplete: () => this.playInternalVideo(i),
            },
            timeLabel,
          );
        }
        // Fade out all slides except last one
        if (featuresArray.length > i + 1) {
          oTimeline.to(feature, { opacity: 0, duration: fadeInOutAnimationDuration / 2 }, '>');
        }
      }
    });

    return oTimeline;
  }

  getNonStackedSlidesTimeOffset(animationDuration) {
    let slidesTimeOffset = animationDuration + animationDuration / 2;
    if (MediaQuery.above('lg')) {
      slidesTimeOffset = animationDuration;
      const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      if (vh <= 950) {
        animationDuration = animationDuration + animationDuration / 3;
      }
    }
    if (MediaQuery.above('xl')) {
      slidesTimeOffset = animationDuration / 1.5;
      const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      if (vh <= 950) {
        animationDuration = animationDuration + animationDuration / 3;
      }
    }
    return slidesTimeOffset;
  }

  getNonStackedAnimationDuration() {
    let animationDuration = 0.25;
    if (MediaQuery.above('lg')) {
      animationDuration = 1.5;
    }
    return animationDuration;
  }

  playInternalVideo(index) {
    const arrMediaItems = Array.from(this.element.querySelectorAll('.feature-scroll__media-item')),
      currItem = arrMediaItems[index];

    if (currItem != null) {
      // Check if it contains a video
      if (currItem.classList.contains('has-video')) {
        const oVideo = currItem.querySelector('video');
        if (oVideo != null) {
          let isPlaying =
            oVideo.currentTime > 0 && !oVideo.paused && !oVideo.ended && oVideo.readyState > 2;

          if (!isPlaying) {
            oVideo.play();
          }
        }
      }
    }
  }

  pauseInternalVideo(index) {
    const arrMediaItems = Array.from(this.element.querySelectorAll('.feature-scroll__media-item')),
      currItem = arrMediaItems[index];

    if (currItem != null) {
      // Check if it contains a video
      if (currItem.classList.contains('has-video')) {
        const oVideo = currItem.querySelector('video');
        if (oVideo != null) {
          oVideo.pause();
          oVideo.load();
        }
      }
    }
  }

  getSceneDuration() {
    return 3000;
  }

  getFeaturesAnimationTimeOffset(index, duration) {
    if (index === 0) {
      return 0;
    }
    return index * duration - duration / 2;
  }

  getFeaturesAnimationYOffsetFromDesktop() {
    return window.innerHeight - window.innerHeight * 0.3;
  }

  getFeaturesAnimationYOffsetFromMobile(elem) {
    const elemBottom = elem.getBoundingClientRect().bottom;
    const elemTop = elem.getBoundingClientRect().top;
    const elemHeight = Math.abs(elemBottom - elemTop);
    return elemHeight;
  }

  getFeaturesAnimationYOffsetToMobile(elem) {
    const translation = -elem.offsetTop;
    return translation;
  }

  getFeaturesAnimationYOffsetToDesktop() {
    return window.innerHeight * 0.08;
  }

  getCenteredVerticalPosition(elem) {
    const menuOffset = Math.ceil(this.offset);
    const windowHeight = window.innerHeight;
    const elemBottom = elem.getBoundingClientRect().bottom;
    const elemTop = elem.getBoundingClientRect().top;
    const elemHeight = Math.abs(elemBottom - elemTop);
    const contentAreaHeight = windowHeight - menuOffset;

    return contentAreaHeight / 2 - elemHeight / 2;
  }

  /**
   * Animate items based on "stacked"-version
   * @param {Object} oTimeline - Timeline for animations
   */
  featuresListAnimationStacked(oTimeline) {
    let animationDuration = MediaQuery.above('lg') ? 0.25 : 0.2,
      timeLabel = 'headingSlideUpStartTime-=0.2',
      offset = 0;

    const arrFeatureItems = Array.from(this.featuresListElems);

    arrFeatureItems.forEach((el, index) => {
      if (index != 0) {
        offset = '-' + this.getFeatureOffsetHeight(arrFeatureItems, index);
        timeLabel = 'featureLabel' + index;
        oTimeline.to(
          arrFeatureItems[index - 1].querySelector('.feature-scroll__listitem__content'),
          {
            duration: animationDuration,
            opacity: 0,
            ease: 'power2.out',
          },
          timeLabel,
        );
      }

      oTimeline.to(
        el,
        {
          duration: animationDuration,
          opacity: 1,
          top: offset,
        },
        timeLabel,
      );
    });

    return oTimeline;
  }

  /**
   *
   **/
  getFeatureOffsetHeight(arrElements, index) {
    let offset = 0;

    arrElements.some((el, i) => {
      const oContent = el.querySelector('.feature-scroll__listitem__content');

      if (oContent != null) {
        offset += oContent.getBoundingClientRect().height;
      }

      return i === index - 1;
    });

    return offset;
  }

  get isStacked() {
    return this.element.classList.contains('feature-scroll--stacked');
  }

  get introElem() {
    return this.element.querySelector('.feature-scroll__intro');
  }

  get headingTextElem() {
    return this.element.querySelector('.feature-scroll__heading');
  }

  get mediaContainerElem() {
    return this.element.querySelector('.feature-scroll__media-container');
  }

  get imageElem() {
    return this.element.querySelector('.feature-scroll__image');
  }

  get listItemMediaElems() {
    return this.element.querySelectorAll('.feature-scroll__listitem__media');
  }

  get featuresListElem() {
    return this.element.getElementsByClassName('feature-scroll__list')[0];
  }

  get featuresListElems() {
    return this.element.getElementsByClassName('feature-scroll__listitem');
  }

  get externalVideosElems() {
    return this.element.getElementsByClassName('feature-scroll__listitem__externalvideo');
  }

  get contentElem() {
    return this.element.getElementsByClassName('feature-scroll__content')[0];
  }
}
